/**
 * ChangeLog Firebase Service
 * 
 * Tracks all changes to goals, milestones, and videos.
 * Provides a chronological history of the user's journey.
 */

import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../../firebase';
import { ChangeLogEntry, ChangeLogAction } from '../types';

const COLLECTION_NAME = 'changeLogs';

/**
 * Create a new changelog entry
 */
export async function logChange(
  ownerId: string,
  goalId: string,
  action: ChangeLogAction,
  entityType: 'goal' | 'milestone' | 'video' | 'journey',
  options?: {
    entityId?: string;
    entityTitle?: string;
    previousValue?: string;
    newValue?: string;
    metadata?: Record<string, any>;
  }
): Promise<string> {
  // Build entry object, filtering out undefined values (Firestore doesn't accept undefined)
  const entry: Record<string, any> = {
    ownerId,
    goalId,
    action,
    entityType,
    createdAt: Date.now(),
  };
  
  // Only add optional fields if they have values
  if (options?.entityId !== undefined) entry.entityId = options.entityId;
  if (options?.entityTitle !== undefined) entry.entityTitle = options.entityTitle;
  if (options?.previousValue !== undefined) entry.previousValue = options.previousValue;
  if (options?.newValue !== undefined) entry.newValue = options.newValue;
  if (options?.metadata !== undefined) entry.metadata = options.metadata;

  const docRef = await addDoc(collection(db, COLLECTION_NAME), entry);
  return docRef.id;
}

/**
 * Get all changelog entries for a goal
 */
export async function getChangeLogByGoal(
  ownerId: string,
  goalId: string
): Promise<ChangeLogEntry[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId),
    where('goalId', '==', goalId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as ChangeLogEntry[];
}

/**
 * Get all changelog entries for a user (across all goals)
 */
export async function getChangeLogByOwner(
  ownerId: string
): Promise<ChangeLogEntry[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as ChangeLogEntry[];
}

// =============================================================================
// CONVENIENCE FUNCTIONS FOR SPECIFIC ACTIONS
// =============================================================================

/**
 * Log goal creation
 */
export async function logGoalCreated(
  ownerId: string,
  goalId: string,
  goalTitle: string
): Promise<string> {
  return logChange(ownerId, goalId, 'goal_created', 'goal', {
    entityId: goalId,
    entityTitle: goalTitle,
  });
}

/**
 * Log goal title change
 */
export async function logGoalTitleChanged(
  ownerId: string,
  goalId: string,
  previousTitle: string,
  newTitle: string
): Promise<string> {
  return logChange(ownerId, goalId, 'goal_title_changed', 'goal', {
    entityId: goalId,
    previousValue: previousTitle,
    newValue: newTitle,
  });
}

/**
 * Log goal start date change
 */
export async function logGoalStartDateChanged(
  ownerId: string,
  goalId: string,
  previousDate: string,
  newDate: string
): Promise<string> {
  return logChange(ownerId, goalId, 'goal_start_date_changed', 'goal', {
    entityId: goalId,
    previousValue: previousDate,
    newValue: newDate,
  });
}

/**
 * Log goal end date change
 */
export async function logGoalEndDateChanged(
  ownerId: string,
  goalId: string,
  previousDate: string,
  newDate: string
): Promise<string> {
  return logChange(ownerId, goalId, 'goal_end_date_changed', 'goal', {
    entityId: goalId,
    previousValue: previousDate,
    newValue: newDate,
  });
}

/**
 * Log milestone creation
 */
export async function logMilestoneCreated(
  ownerId: string,
  goalId: string,
  milestoneId: string,
  milestoneTitle: string
): Promise<string> {
  return logChange(ownerId, goalId, 'milestone_created', 'milestone', {
    entityId: milestoneId,
    entityTitle: milestoneTitle,
  });
}

/**
 * Log milestone title change
 */
export async function logMilestoneTitleChanged(
  ownerId: string,
  goalId: string,
  milestoneId: string,
  previousTitle: string,
  newTitle: string
): Promise<string> {
  return logChange(ownerId, goalId, 'milestone_title_changed', 'milestone', {
    entityId: milestoneId,
    previousValue: previousTitle,
    newValue: newTitle,
  });
}

/**
 * Log milestone date change
 */
export async function logMilestoneDateChanged(
  ownerId: string,
  goalId: string,
  milestoneId: string,
  milestoneTitle: string,
  previousDate: string,
  newDate: string
): Promise<string> {
  return logChange(ownerId, goalId, 'milestone_date_changed', 'milestone', {
    entityId: milestoneId,
    entityTitle: milestoneTitle,
    previousValue: previousDate,
    newValue: newDate,
  });
}

/**
 * Log milestone completion
 */
export async function logMilestoneCompleted(
  ownerId: string,
  goalId: string,
  milestoneId: string,
  milestoneTitle: string
): Promise<string> {
  return logChange(ownerId, goalId, 'milestone_completed', 'milestone', {
    entityId: milestoneId,
    entityTitle: milestoneTitle,
  });
}

/**
 * Log milestone deletion
 */
export async function logMilestoneDeleted(
  ownerId: string,
  goalId: string,
  milestoneId: string,
  milestoneTitle: string
): Promise<string> {
  return logChange(ownerId, goalId, 'milestone_deleted', 'milestone', {
    entityId: milestoneId,
    entityTitle: milestoneTitle,
  });
}

/**
 * Log video recorded
 */
export async function logVideoRecorded(
  ownerId: string,
  goalId: string,
  videoType: 'start' | 'reflection',
  dayNumber?: number
): Promise<string> {
  const entityTitle = videoType === 'start' 
    ? 'Start video' 
    : `Video D${dayNumber}`;
  
  return logChange(ownerId, goalId, 'video_recorded', 'video', {
    entityTitle,
    metadata: { videoType, dayNumber },
  });
}

/**
 * Log video re-recorded (with count)
 */
export async function logVideoRerecorded(
  ownerId: string,
  goalId: string,
  videoType: 'start' | 'reflection',
  dayNumber?: number,
  rerecordCount?: number
): Promise<string> {
  const entityTitle = videoType === 'start' 
    ? 'Start video' 
    : `Video D${dayNumber}`;
  
  return logChange(ownerId, goalId, 'video_rerecorded', 'video', {
    entityTitle,
    metadata: { videoType, dayNumber, rerecordCount },
  });
}

/**
 * Log video deleted
 */
export async function logVideoDeleted(
  ownerId: string,
  goalId: string,
  videoType: 'start' | 'reflection',
  dayNumber?: number
): Promise<string> {
  const entityTitle = videoType === 'start' 
    ? 'Start video' 
    : `Video D${dayNumber}`;
  
  return logChange(ownerId, goalId, 'video_deleted', 'video', {
    entityTitle,
    metadata: { videoType, dayNumber },
  });
}

/**
 * Log journey started
 */
export async function logJourneyStarted(
  ownerId: string,
  goalId: string,
  goalTitle: string
): Promise<string> {
  return logChange(ownerId, goalId, 'journey_started', 'journey', {
    entityTitle: goalTitle,
  });
}

/**
 * Log journey completed
 */
export async function logJourneyCompleted(
  ownerId: string,
  goalId: string,
  goalTitle: string
): Promise<string> {
  return logChange(ownerId, goalId, 'journey_completed', 'journey', {
    entityTitle: goalTitle,
  });
}
